"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { enhanceMagentoCarousels } from "./pbMagentoCarousel";

/**
 * Invisible marker rendered once inside every `.pb-root`. It upgrades the
 * server-rendered Page Builder markup without wrapping it in a client
 * component (so the whole tree stays RSC):
 *
 * - internal `<a>` clicks (buttons, banners, images, links inside TinyMCE
 *   text) navigate client-side with the App Router instead of a full reload,
 * - exposes the scrollbar-free viewport width as `--pb-viewport-width`, used
 *   by full-width / full-bleed rows to break out of the page container,
 * - adds arrows / dots / autoplay to carousels built from `x-magento-init`
 *   slick configs in admin HTML (`pbMagentoCarousel.ts`).
 */
export function PbEnhancer() {
  const ref = useRef<HTMLSpanElement>(null);
  const router = useRouter();

  useEffect(() => {
    const root = ref.current?.closest<HTMLElement>(".pb-root");
    if (!root) return;

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || !root.contains(anchor)) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download") || anchor.dataset.pbNative !== undefined) return;
      const href = anchor.getAttribute("href") ?? "";
      if (!href || href.startsWith("#") || /^(?:mailto|tel|sms|javascript):/i.test(href)) return;
      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      event.preventDefault();
      router.push(`${url.pathname}${url.search}${url.hash}`);
    };

    const syncViewport = () => {
      root.style.setProperty("--pb-viewport-width", `${document.documentElement.clientWidth}px`);
    };

    syncViewport();
    const disposeCarousels = enhanceMagentoCarousels(root);
    root.addEventListener("click", onClick);
    window.addEventListener("resize", syncViewport, { passive: true });
    return () => {
      disposeCarousels();
      root.removeEventListener("click", onClick);
      window.removeEventListener("resize", syncViewport);
    };
  }, [router]);

  return <span ref={ref} hidden data-pb-enhancer="" />;
}
